// src/components/SquareCatalogMap.jsx
//
// Collapsible card that lets restaurant owners map their TableChat dishes
// to Square catalog items. Appears in the portal Menu page below the dish list.
//
// When a mapping is saved, future orders use the Square catalog_object_id
// instead of free-form line items — inventory decrements and sales reports
// roll up correctly by category (Option A accuracy upgrade).

import React, { useState, useEffect } from 'react';
import { portalFetch } from '../lib/supabase';

function fmtMoney(n) { return n != null ? `$${parseFloat(n).toFixed(2)}` : ''; }

export default function SquareCatalogMap({ categories, restaurant }) {
  const [open, setOpen]                 = useState(false);
  const [squareItems, setSquareItems]   = useState([]);  // Square catalog items
  const [mappings, setMappings]         = useState({});  // { dishId: squareCatalogObjectId }
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [error, setError]               = useState('');
  const [catalogError, setCatalogError] = useState('');

  // Flatten all dishes from all categories for the mapping table
  const allDishes = (categories || []).flatMap(cat =>
    (cat.dishes || []).map(d => ({ ...d, categoryName: cat.display_name }))
  );

  // Load Square catalog and existing dish mappings when opened
  useEffect(() => {
    if (!open || squareItems.length > 0) return;
    loadCatalog();
  }, [open]);

  const loadCatalog = async () => {
    setLoading(true);
    setCatalogError('');
    try {
      // Fetch Square catalog items
      const catalogData = await portalFetch('/square/catalog');
      if (catalogData?.error) {
        setCatalogError(catalogData.error);
        setLoading(false);
        return;
      }
      setSquareItems(catalogData?.items || []);

      // Fetch existing dish mappings from the portal menu endpoint
      const menuData = await portalFetch('/menu');
      if (menuData?.categories) {
        const existingMappings = {};
        menuData.categories.forEach(cat => {
          (cat.dishes || []).forEach(dish => {
            if (dish.square_catalog_object_id) {
              existingMappings[dish.id] = dish.square_catalog_object_id;
            }
          });
        });
        setMappings(existingMappings);
      }
    } catch (err) {
      setCatalogError('Failed to load Square catalog. Check your Square credentials in Settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (dishId, squareCatalogObjectId) => {
    setMappings(prev => ({ ...prev, [dishId]: squareCatalogObjectId }));
    setSaved(false);
  };

  const saveMappings = async () => {
    setError('');
    setSaving(true);

    const mappingArray = Object.entries(mappings).map(([dish_id, square_catalog_object_id]) => ({
      dish_id,
      square_catalog_object_id: square_catalog_object_id || null,
    }));

    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'square_catalog_map',
        updates: { mappings: mappingArray },
      }),
    });

    setSaving(false);
    if (result?.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setError(result?.error || 'Failed to save mappings. Please try again.');
    }
  };

  // Count how many dishes are mapped
  const mappedCount = allDishes.filter(d => mappings[d.id]).length;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: '1.5rem', overflow: 'hidden', background: 'white' }}>

      {/* Header — click to expand */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '1rem 1.4rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          background: open ? 'var(--warm)' : 'white',
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <span style={{ fontSize: '1.1rem' }}>🔗</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
              Map to Square catalog
              {mappedCount > 0 && (
                <span style={{ marginLeft: 8, fontSize: '0.75rem', background: '#D1FAE5', color: '#065F46', padding: '0.15rem 0.5rem', borderRadius: 10, fontWeight: 700 }}>
                  {mappedCount}/{allDishes.length} mapped
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
              Link dishes to your Square catalog for accurate inventory and sales reporting
            </div>
          </div>
        </div>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{open ? '▲' : '▾'}</span>
      </div>

      {open && (
        <div style={{ padding: '1.2rem 1.4rem' }}>

          {/* Info banner */}
          <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1.2rem', fontSize: '0.8rem', color: '#0C447C', lineHeight: 1.6 }}>
            <strong>Why map dishes?</strong> Unmapped dishes are sent to Square as custom line items —
            they appear in orders but don't decrement inventory or roll up in category reports.
            Mapped dishes use your Square catalog directly, so inventory and reporting stay accurate.
            Leave a dish unmapped if it doesn't exist in your Square catalog.
          </div>

          {/* Loading state */}
          {loading && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>
              Loading Square catalog...
            </div>
          )}

          {/* Catalog error */}
          {catalogError && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#991B1B' }}>
              {catalogError}
              <button
                onClick={loadCatalog}
                style={{ display: 'block', marginTop: 6, background: 'none', border: 'none', color: '#991B1B', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}
              >
                Try again
              </button>
            </div>
          )}

          {/* Mapping table */}
          {!loading && !catalogError && allDishes.length > 0 && (
            <>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem', padding: '0 0.2rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  TableChat dish
                </div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Square catalog item
                </div>
              </div>

              {/* Dish rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.2rem' }}>
                {allDishes.map(dish => {
                  const currentMapping = mappings[dish.id] || '';
                  const isMapped = !!currentMapping;

                  return (
                    <div
                      key={dish.id}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
                        alignItems: 'center', padding: '0.65rem 0.8rem',
                        background: isMapped ? '#F0FDF4' : 'var(--warm)',
                        borderRadius: 8,
                        border: `1px solid ${isMapped ? '#86EFAC' : 'var(--border)'}`,
                      }}
                    >
                      {/* Left — TableChat dish info */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {dish.name}
                        </div>
                        <div style={{ fontSize: '0.73rem', color: 'var(--muted)', marginTop: 2 }}>
                          {dish.categoryName} · {fmtMoney(dish.price)}
                          {isMapped && <span style={{ marginLeft: 6, color: '#16A34A', fontWeight: 700 }}>✓ mapped</span>}
                        </div>
                      </div>

                      {/* Right — Square catalog dropdown */}
                      <select
                        value={currentMapping}
                        onChange={e => handleMappingChange(dish.id, e.target.value)}
                        style={{
                          width: '100%', padding: '0.5rem 0.7rem',
                          border: `1.5px solid ${isMapped ? '#86EFAC' : 'var(--border)'}`,
                          borderRadius: 7, fontSize: '0.82rem',
                          outline: 'none', color: 'var(--ink)', background: 'white',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="">— Not mapped (free-form) —</option>
                        {squareItems.length === 0 && (
                          <option disabled>No Square catalog items found</option>
                        )}
                        {squareItems.map(item => (
                          item.variations.length <= 1 ? (
                            // Single variation — show item directly
                            <option key={item.default_variation_id} value={item.default_variation_id}>
                              {item.name}{item.variations[0]?.price ? ` · $${item.variations[0].price}` : ''}
                            </option>
                          ) : (
                            // Multiple variations — show as optgroup
                            <optgroup key={item.id} label={item.name}>
                              {item.variations.map(v => (
                                <option key={v.id} value={v.id}>
                                  {v.name}{v.price ? ` · $${v.price}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                {mappedCount} of {allDishes.length} dishes mapped to Square catalog.
                {allDishes.length - mappedCount > 0 && ` ${allDishes.length - mappedCount} will use free-form line items.`}
              </div>

              {/* Error */}
              {error && (
                <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#991B1B' }}>
                  {error}
                </div>
              )}

              {/* Save button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  onClick={saveMappings}
                  disabled={saving}
                  style={{
                    background: 'var(--orange)', color: 'white', border: 'none',
                    borderRadius: 8, padding: '0.75rem 1.6rem',
                    fontWeight: 700, fontSize: '0.875rem',
                    cursor: saving ? 'default' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Save mappings'}
                </button>
                {saved && (
                  <span style={{ color: '#16A34A', fontWeight: 700, fontSize: '0.875rem' }}>
                    ✓ Saved — future orders will use catalog IDs where mapped
                  </span>
                )}
              </div>
            </>
          )}

          {/* Empty state */}
          {!loading && !catalogError && allDishes.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>
              No dishes found. Add menu items first, then come back to map them.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
